import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 品牌介绍与联系方式 - 韩漫网';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        '了解韩漫网品牌故事与发展历程。韩漫网是国内领先的免费韩国漫画在线阅读平台，提供海量高清韩漫资源，每日更新热门连载。查看联系方式与平台数据。'
      );
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const response = await fetch('http://yxhaomai.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请检查信息后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 韩漫网',
            description: '了解韩漫网品牌故事与发展历程。韩漫网是国内领先的免费韩国漫画在线阅读平台。',
            url: 'http://yxhaomai.com/about',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '韩漫网',
            url: 'http://yxhaomai.com/',
            description: '免费韩国漫画在线阅读平台，提供海量人气韩漫资源',
            foundingDate: '2020',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'hello@yxhaomai.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://yxhaomai.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://yxhaomai.com/about' },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                关于韩漫网
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
                国内领先的免费韩国漫画在线阅读平台
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
                <div className="lg:w-1/2">
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-4">
                    品牌故事
                  </span>
                  <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-6 leading-tight">
                    让每一位漫画爱好者
                    <br />
                    都能免费畅享韩漫
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      韩漫网成立于2020年，是国内最早专注于韩国漫画在线阅读的平台之一。自创立以来，我们始终秉持"免费、高清、丰富"的理念，致力于为中文读者提供最优质的韩国漫画阅读体验。
                    </p>
                    <p>
                      平台目前已收录超过10,000部韩国漫画作品，涵盖爱情、奇幻、动作、悬疑、搞笑、剧情、恐怖等七大分类，每日更新热门连载，满足不同读者的口味需求。我们与多家韩国知名漫画平台保持紧密合作，确保漫画资源及时更新。
                    </p>
                    <p>
                      韩漫网采用先进的图片加载技术，在保证高清画质的同时实现快速加载，让读者无论使用电脑、平板还是手机，都能享受流畅舒适的阅读体验。全站漫画均免费开放，无需付费订阅，真正让每一位漫画爱好者都能畅享韩漫的魅力。
                    </p>
                  </div>
                </div>

                <div className="lg:w-1/2">
                  <div className="grid grid-cols-2 gap-5 md:gap-6">
                    <div className="rounded-2xl bg-background-100 p-6 md:p-8">
                      <div className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-2">10,000+</div>
                      <p className="text-xs md:text-sm text-foreground-500">漫画资源总量</p>
                    </div>
                    <div className="rounded-2xl bg-accent-100 p-6 md:p-8">
                      <div className="text-3xl md:text-4xl font-bold text-accent-700 font-heading mb-2">50万+</div>
                      <p className="text-xs md:text-sm text-accent-600">日活跃读者</p>
                    </div>
                    <div className="rounded-2xl bg-accent-100 p-6 md:p-8">
                      <div className="text-3xl md:text-4xl font-bold text-accent-700 font-heading mb-2">每日更新</div>
                      <p className="text-xs md:text-sm text-accent-600">热门连载频率</p>
                    </div>
                    <div className="rounded-2xl bg-background-100 p-6 md:p-8">
                      <div className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-2">200+</div>
                      <p className="text-xs md:text-sm text-foreground-500">签约作者数量</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-4">
                  核心特色
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
                  为什么选择韩漫网
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                <div className="rounded-2xl overflow-hidden bg-background-50 group cursor-default">
                  <div className="h-48 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20fast%20update%20concept%20art%2C%20abstract%20speed%20lines%20with%20clock%2C%20dynamic%20illustration%20style%2C%20warm%20energetic%20colors%2C%20minimal%20composition&width=600&height=400&seq=feature-fast-update&orientation=landscape"
                      alt="极速更新"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-foreground-900 mb-2">极速更新</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">与韩国平台同步更新，热门连载每日上新，不再错过任何精彩章节。</p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden bg-background-50 group cursor-default">
                  <div className="h-48 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20high%20quality%20art%2C%20crystal%20clear%20illustration%20detail%2C%20sharp%20lines%20and%20vivid%20colors%2C%20professional%20webtoon%20quality%2C%20clean%20minimal%20composition&width=600&height=400&seq=feature-hd-quality&orientation=landscape"
                      alt="高清画质"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-foreground-900 mb-2">高清画质</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">所有漫画均为高清画质，每一页细节清晰可见，提供沉浸式阅读体验。</p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden bg-background-50 group cursor-default">
                  <div className="h-48 overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20free%20reading%20concept%2C%20open%20book%20with%20glowing%20pages%2C%20warm%20and%20inviting%20atmosphere%2C%20soft%20golden%20light%2C%20minimal%20artistic%20style&width=600&height=400&seq=feature-free-all&orientation=landscape"
                      alt="全站免费"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-foreground-900 mb-2">全站免费</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">所有漫画资源完全免费，无需付费订阅，让每位读者都能畅享韩漫魅力。</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
                <div className="lg:w-1/2">
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-4">
                    联系方式
                  </span>
                  <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-6 leading-tight">
                    联系我们
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className="ri-mail-line text-accent-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服邮箱</h4>
                        <p className="text-sm text-foreground-500">hello@yxhaomai.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-accent-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                        <p className="text-sm text-foreground-500">北京市朝阳区望京SOHO</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className="ri-time-line text-accent-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                        <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-1/2">
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                    <h3 className="text-lg font-bold text-foreground-900 mb-5">发送消息</h3>
                    {formStatus === 'success' ? (
                      <div className="text-center py-8">
                        <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                          <i className="ri-check-line text-accent-600 text-2xl"></i>
                        </div>
                        <p className="text-foreground-900 font-semibold mb-1">消息已发送！</p>
                        <p className="text-sm text-foreground-500">感谢您的来信，我们会尽快回复。</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 pointer-events-none"
                        />
                        <div>
                          <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            姓名
                          </label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            邮箱
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            消息
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                            placeholder="请输入您的消息（最多500字符）"
                          ></textarea>
                        </div>
                        {formStatus === 'error' && formError && (
                          <p className="text-xs text-primary-600">{formError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={formStatus === 'loading'}
                          className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-secondary-300 text-background-50 font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          {formStatus === 'loading' ? '发送中...' : '发送消息'}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20community%20reading%20atmosphere%2C%20group%20of%20people%20enjoying%20comics%20together%2C%20warm%20cozy%20environment%2C%20soft%20lighting%2C%20abstract%20illustration%20style%2C%20golden%20hour%20tones%2C%20friendly%20vibes&width=1400&height=500&seq=about-cta-community&orientation=landscape"
                  alt="加入韩漫社区"
                  className="w-full h-[300px] md:h-[380px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 to-foreground-950/20"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                  <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight mb-3">
                    加入韩漫大家庭
                  </h2>
                  <p className="text-foreground-300 text-sm md:text-base mb-8 max-w-md">
                    与50万漫画爱好者一起，探索韩国漫画的无限魅力
                  </p>
                  <Link
                    to="/category/all"
                    className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    开始探索
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}